import { create } from 'zustand';

export interface FileEntry {
  name: string;
  path: string; // Full path for ADB, relative path for Local (from root handle)
  type: 'file' | 'directory';
  size: number;
  modifiedTime: Date;
  handle?: FileSystemHandle; // For local files
  provider: 'adb' | 'local';
}

export type SortField = 'name' | 'size' | 'modifiedTime';
export type SortOrder = 'asc' | 'desc';

interface FileSideState {
  path: string;
  files: FileEntry[];
  selectedPaths: string[];
  lastSelectedPath: string | null;
  directoryHandle: FileSystemDirectoryHandle | null; // For local directories
  directoryHandleStack: FileSystemDirectoryHandle[]; // Stack for parent directories
  sortField: SortField;
  sortOrder: SortOrder;
  isSearching: boolean;
  isRefreshing: boolean;
}

interface FileState {
  left: FileSideState;
  right: FileSideState;
  
  // Actions
  setPath: (side: 'left' | 'right', path: string) => void;
  setFiles: (side: 'left' | 'right', files: FileEntry[]) => void;
  setDirectoryHandle: (side: 'left' | 'right', handle: FileSystemDirectoryHandle | null, pushToStack?: boolean) => void;
  toggleSelect: (side: 'left' | 'right', path: string, multi?: boolean, range?: boolean) => void;
  selectAll: (side: 'left' | 'right') => void;
  clearSelection: (side: 'left' | 'right') => void;
  removeSelectedFromList: (side: 'left' | 'right') => void;
  refresh: (side: 'left' | 'right') => Promise<void>;
  setSort: (side: 'left' | 'right', field: SortField, order?: SortOrder) => void;
  navigateUp: (side: 'left' | 'right') => void;
  navigateUpLocal: (side: 'left' | 'right') => void;
  setSearching: (side: 'left' | 'right', isSearching: boolean) => void;
  setRefreshing: (side: 'left' | 'right', isRefreshing: boolean) => void;
}

const initialSideState: FileSideState = {
  path: '',
  files: [],
  selectedPaths: [],
  lastSelectedPath: null,
  directoryHandle: null,
  directoryHandleStack: [],
  sortField: 'name',
  sortOrder: 'asc',
  isSearching: false,
  isRefreshing: false,
};

export const useFileStore = create<FileState>((set) => ({
  left: { ...initialSideState },
  right: { ...initialSideState },

  setPath: (side, path) => set((state) => ({
    [side]: { ...state[side], path }
  })),

  setFiles: (side, files) => set((state) => {
    const sideState = state[side];
    // Sort files by name first (directories first, then files), then apply current sort
    const sortedFiles = [...files].sort((a, b) => {
      // Directories always come first
      if (a.type === 'directory' && b.type !== 'directory') return -1;
      if (a.type !== 'directory' && b.type === 'directory') return 1;

      // Then sort by name
      return a.name.localeCompare(b.name, 'zh-CN', { numeric: true });
    });

    return {
      [side]: { ...sideState, files: sortedFiles }
    };
  }),

  setDirectoryHandle: (side, handle, pushToStack = false) => set((state) => {
    const sideState = state[side];
    const newStack = pushToStack && sideState.directoryHandle
      ? [...sideState.directoryHandleStack, sideState.directoryHandle]
      : sideState.directoryHandleStack;
    
    return {
      [side]: { ...sideState, directoryHandle: handle, directoryHandleStack: newStack }
    };
  }),

  navigateUpLocal: (side: 'left' | 'right') => set((state) => {
    const sideState = state[side];
    if (sideState.directoryHandleStack.length === 0) {
      return state;
    }
    
    const newStack = [...sideState.directoryHandleStack];
    const parentHandle = newStack.pop()!;
    
    return {
      [side]: {
        ...sideState,
        directoryHandle: parentHandle,
        directoryHandleStack: newStack,
        path: parentHandle.name,
        selectedPaths: []
      }
    };
  }),

  toggleSelect: (side, path, multi = false, range = false) => set((state) => {
    const sideState = state[side];
    let newSelected = [...sideState.selectedPaths];
    
    if (range && sideState.lastSelectedPath && sideState.files.length > 0) {
      const lastIdx = sideState.files.findIndex(f => f.path === sideState.lastSelectedPath);
      const currIdx = sideState.files.findIndex(f => f.path === path);
      
      if (lastIdx !== -1 && currIdx !== -1) {
        const start = Math.min(lastIdx, currIdx);
        const end = Math.max(lastIdx, currIdx);
        const rangePaths = sideState.files.slice(start, end + 1).map(f => f.path);
        
        if (multi) {
          newSelected = Array.from(new Set([...newSelected, ...rangePaths]));
        } else {
          newSelected = rangePaths;
        }
      }
    } else if (multi) {
      const index = newSelected.indexOf(path);
      if (index === -1) {
        newSelected.push(path);
      } else {
        newSelected.splice(index, 1);
      }
    } else {
      newSelected = [path];
    }
    
    return {
      [side]: {
        ...sideState,
        selectedPaths: newSelected,
        lastSelectedPath: path
      }
    };
  }),

  selectAll: (side) => set((state) => {
    const sideState = state[side];
    return {
      [side]: { ...sideState, selectedPaths: sideState.files.map(f => f.path) }
    };
  }),

  clearSelection: (side) => set((state) => ({
    [side]: { ...state[side], selectedPaths: [] }
  })),

  removeSelectedFromList: (side) => set((state) => {
    const sideState = state[side];
    const newFiles = sideState.files.filter(f => !sideState.selectedPaths.includes(f.path));
    return {
      [side]: { ...sideState, files: newFiles, selectedPaths: [] }
    };
  }),

  refresh: async (side) => {
    // This will be implemented in the component or a hook that has access to services
    console.log(`Refreshing ${side} side`);
  },

  navigateUp: (side) => set((state) => {
    const sideState = state[side];
    if (!sideState.path || sideState.path === '/' || sideState.path === 'adb:///') {
      return state;
    }
    
    const isAdb = sideState.path.startsWith('adb://');
    const rawPath = isAdb ? sideState.path.replace('adb://', '') : sideState.path;
    
    const parts = rawPath.split('/').filter(Boolean);
    parts.pop();
    
    const newPath = isAdb
      ? `adb://${parts.length > 0 ? '/' + parts.join('/') : '/'}`
      : parts.join('/') || '/';

    return {
      [side]: { ...sideState, path: newPath, selectedPaths: [] }
    };
  }),

  setSort: (side, field, order) => set((state) => {
    const sideState = state[side];
    const newOrder = order || (sideState.sortField === field ? (sideState.sortOrder === 'asc' ? 'desc' : 'asc') : 'asc');

    // Sort files: directories first, then by selected field
    const sortedFiles = [...sideState.files].sort((a, b) => {
      // Directories always come first
      if (a.type === 'directory' && b.type !== 'directory') return -1;
      if (a.type !== 'directory' && b.type === 'directory') return 1;

      // Then sort by selected field
      let comparison = 0;
      if (field === 'name') {
        comparison = a.name.localeCompare(b.name, 'zh-CN', { numeric: true });
      } else if (field === 'size') {
        comparison = a.size - b.size;
      } else if (field === 'modifiedTime') {
        comparison = a.modifiedTime.getTime() - b.modifiedTime.getTime();
      }
      return newOrder === 'asc' ? comparison : -comparison;
    });

    return {
      [side]: {
        ...sideState,
        sortField: field,
        sortOrder: newOrder,
        files: sortedFiles
      }
    };
  }),

  setSearching: (side, isSearching) => set((state) => ({
    [side]: { ...state[side], isSearching }
  })),

  setRefreshing: (side, isRefreshing) => set((state) => ({
    [side]: { ...state[side], isRefreshing }
  })),
}));
